'use client'

import {useState} from 'react'
import {ChevronDown, Copy, Minus, Plus} from 'lucide-react'
import {toast} from 'react-toastify'

import notifyError from '@/utils/notifyError'
import {formatDateFromStringWithHour} from '@/utils/formatDate'
import type {OrderJob} from '@/types/order'

interface OrderJobsPanelProps {
    jobs: OrderJob[]
}

// Support needs the raw job id to look a job up in Lumialase, plus the record behind
// it when a job misbehaves. Jobs belong to a line item, so this sits in that line
// item's dialog and reads the order payload the page already loaded -- no requests
// of its own.
const statusStyles: Record<string, string> = {
    done: 'bg-[#ECFDF3] text-[#027A48]',
    job_created: 'bg-gray-100 text-gray-700',
    job_marking: 'bg-[#EFF8FF] text-[#175CD3]',
    job_failed: 'bg-[#FEF3F2] text-[#D92D20]',
    job_partially_completed: 'bg-[#FFFAEB] text-[#B54708]',
}

// formatDateFromStringWithHour throws on an invalid date, and a job's last_changed_at
// can be absent or carry Go's zero time when it has never been touched -- either would
// take the whole dialog down.
const ZERO_TIME_PREFIX = '0001-01-01'

const formatJobDate = (value: string) => {
    if (!value || value.startsWith(ZERO_TIME_PREFIX)) return '—'

    try {
        return formatDateFromStringWithHour(value)
    } catch {
        return '—'
    }
}

const getStatusLabel = (status: string) => {
    if (!status) return 'Unknown'
    const normalized = status.replaceAll('_', ' ').trim().toLowerCase()
    return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

const JobRow = ({job}: {job: OrderJob}) => {
    const [isExpanded, setIsExpanded] = useState(false)


    const handleCopyId = async () => {
        try {
            await navigator.clipboard.writeText(job.id)
            toast.success('Job ID copied')
        } catch {
            notifyError('Unable to copy the job ID')
        }
    }

    return (
        <div className="rounded-md border border-gray-200">
            <div className="flex items-center gap-sm p-md">
                <button
                    type="button"
                    onClick={() => setIsExpanded(value => !value)}
                    aria-expanded={isExpanded}
                    className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded border border-gray-300 text-gray-500 transition-colors hover:border-primary-500 hover:text-primary-600"
                >
                    {isExpanded ? <Minus size={14}/> : <Plus size={14}/>}
                    <span className="sr-only">{isExpanded ? 'Hide' : 'Show'} full record for job {job.id}</span>
                </button>

                <div className="min-w-0 flex-grow">
                    <div className="flex items-center gap-xs">
                        <code className="truncate font-mono text-xs text-gray-900">{job.id}</code>
                        <button
                            type="button"
                            onClick={handleCopyId}
                            title="Copy job ID"
                            className="shrink-0 text-gray-400 transition-colors hover:text-primary-600"
                        >
                            <Copy size={12}/>
                            <span className="sr-only">Copy job ID</span>
                        </button>
                    </div>
                    <p className="mt-xs truncate text-xs text-gray-400">
                        SKU {job.sku || '—'}
                        {typeof job.bundle_index === 'number' ? ` · Bundle ${job.bundle_index}` : ''}
                    </p>
                </div>

                <span
                    className={`shrink-0 rounded-full px-sm py-[2px] text-xs font-medium ${statusStyles[job.job_status] ?? 'bg-gray-100 text-gray-700'}`}
                >
                    {getStatusLabel(job.job_status)}
                </span>
            </div>

            {isExpanded && (
                <div className="border-t border-gray-200 bg-gray-50 p-md">
                    <div className="grid grid-cols-3 gap-md">
                        <div>
                            <p className="text-xs text-gray-400">Quantity</p>
                            <p className="mt-xs text-sm font-medium text-gray-900">{job.quantity}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-400">Done</p>
                            <p className="mt-xs text-sm font-medium text-[#027A48]">{job.quantity_done}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-400">Failed</p>
                            <p className={`mt-xs text-sm font-medium ${job.quantity_failed > 0 ? 'text-[#D92D20]' : 'text-gray-900'}`}>
                                {job.quantity_failed}
                            </p>
                        </div>
                    </div>

                    <div className="mt-md grid grid-cols-2 gap-md">
                        <div>
                            <p className="text-xs text-gray-400">Created</p>
                            <p className="mt-xs text-sm text-gray-900">{formatJobDate(job.created_at)}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-400">Last changed</p>
                            <p className="mt-xs text-sm text-gray-900">{formatJobDate(job.last_changed_at)}</p>
                        </div>
                    </div>

                    <p className="mt-md text-xs text-gray-400">Full record</p>
                    <pre className="mt-xs max-h-[280px] overflow-auto rounded border border-gray-200 bg-white p-sm font-mono text-[11px] leading-relaxed text-gray-700">
                        {JSON.stringify(job, null, 2)}
                    </pre>
                </div>
            )}
        </div>
    )
}

const OrderJobsPanel = ({jobs}: OrderJobsPanelProps) => {
    const [isOpen, setIsOpen] = useState(false)

    if (!jobs || jobs.length === 0) return null

    return (
        <div className="mt-xl border-t border-gray-200 pt-lg">
            <button
                type="button"
                onClick={() => setIsOpen(value => !value)}
                aria-expanded={isOpen}
                className="flex w-full items-center gap-sm text-left"
            >
                <ChevronDown
                    size={16}
                    className={`shrink-0 text-gray-400 transition-transform ${isOpen ? '' : '-rotate-90'}`}
                />
                <span className="text-title-md font-medium text-gray-900">Machine Jobs</span>
                <span className="rounded-full bg-gray-100 px-sm py-[2px] text-xs text-gray-600">{jobs.length}</span>
            </button>

            {isOpen && (
                <div className="mt-md flex flex-col gap-sm">
                    {jobs.map(job => <JobRow key={job.id} job={job}/>)}
                </div>
            )}
        </div>
    )
}

export default OrderJobsPanel
